/**
 * Type-safe обёртки модуля заявок гостя (UC-G.05/06/07/08).
 *
 * Все эндпойнты идут через customFetch (инъекция bearer JWT + tenant slug).
 * Бэкенд: `internal/request/interfaces/http`:
 *   GET  /v1/me/requests              — список заявок гостя (ownership на бэке)
 *   GET  /v1/requests/{id}            — карточка + комментарии (IDOR-защита #329:
 *                                       гость видит только свою → 404 чужую)
 *   POST /v1/requests/{id}/cancel     — отмена заявки гостем
 *   POST /v1/requests/{id}/comments   — добавить сообщение в чат заявки
 *   GET  /v1/requests/{id}/sla-status — статус SLA (трекинг)
 */
import { customFetch } from './openapi-fetch.js';
import type {
  AssignableStaffResponse,
  RequestCancelInput,
  RequestComment,
  RequestCommentInput,
  RequestListResponse,
  RequestSLAStatus,
  RequestView,
  RequestWithComments,
} from '@hsc/types';

const BASE = '/requests';

/** GET /v1/me/requests — заявки текущего гостя (новые сверху на бэке). */
export async function listMyRequests(params?: {
  limit?: number;
  offset?: number;
}): Promise<RequestListResponse> {
  const q: Record<string, string | number> = {};
  if (params?.limit !== undefined) q['limit'] = params.limit;
  if (params?.offset !== undefined) q['offset'] = params.offset;
  return customFetch<RequestListResponse>(
    Object.keys(q).length > 0
      ? { url: '/me/requests', method: 'GET', params: q }
      : { url: '/me/requests', method: 'GET' },
  );
}

/** GET /v1/requests/{id} — карточка заявки с комментариями (только своя). */
export async function getRequest(id: string): Promise<RequestWithComments> {
  return customFetch<RequestWithComments>({
    url: `${BASE}/${encodeURIComponent(id)}`,
    method: 'GET',
  });
}

/** POST /v1/requests/{id}/cancel — отмена заявки гостем (с причиной). */
export async function cancelRequest(
  id: string,
  input?: RequestCancelInput,
): Promise<RequestView> {
  return customFetch<RequestView>({
    url: `${BASE}/${encodeURIComponent(id)}/cancel`,
    method: 'POST',
    data: input ?? {},
  });
}

/** POST /v1/requests/{id}/comments — отправить сообщение в чат заявки. */
export async function addRequestComment(
  id: string,
  input: RequestCommentInput,
): Promise<RequestComment> {
  return customFetch<RequestComment>({
    url: `${BASE}/${encodeURIComponent(id)}/comments`,
    method: 'POST',
    data: input,
  });
}

/**
 * GET /v1/requests/{id}/assignable-staff — сотрудники, ПРАВОМОЧНЫЕ исполнять
 * именно эту заявку (отдел категории + общий пул guest_services). §6.2 п.3:
 * дропдаун исполнителя берёт список только отсюда. RBAC: management/staff.
 */
export async function getAssignableStaff(id: string): Promise<AssignableStaffResponse> {
  return customFetch<AssignableStaffResponse>({
    url: `${BASE}/${encodeURIComponent(id)}/assignable-staff`,
    method: 'GET',
  });
}

/** GET /v1/requests/{id}/sla-status — статус SLA для трекинга. */
export async function getRequestSLAStatus(id: string): Promise<RequestSLAStatus> {
  return customFetch<RequestSLAStatus>({
    url: `${BASE}/${encodeURIComponent(id)}/sla-status`,
    method: 'GET',
  });
}
